import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import io from 'socket.io-client';

import VideoCameraOutlined from '@ant-design/icons/VideoCameraOutlined';
import PhoneOutlined from '@ant-design/icons/PhoneOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';

import './GlobalMeetingAlert.css';

// Continuous repeating Teams-style audio chime generator using browser Web Audio API
let ringInterval = null;
let activeAudioCtx = null;

function startContinuousRingtone() {
  stopContinuousRingtone();
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    activeAudioCtx = new AudioCtx();
    if (activeAudioCtx.state === 'suspended') {
      activeAudioCtx.resume().catch(() => {});
    }

    const playTeamsChime = () => {
      if (!activeAudioCtx) return;
      if (activeAudioCtx.state === 'suspended') {
        activeAudioCtx.resume().catch(() => {});
      }
      const ctx = activeAudioCtx;
      const now = ctx.currentTime;

      const playTone = (freq, time, dur, vol = 0.3) => {
        try {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, time);
          gain.gain.setValueAtTime(vol, time);
          gain.gain.exponentialRampToValueAtTime(0.0001, time + dur);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(time);
          osc.stop(time + dur);
        } catch (_) {}
      };

      // MS Teams iconic harmonic chime: G5, B5, D6, G6
      playTone(783.99, now + 0.00, 0.22, 0.32);
      playTone(987.77, now + 0.12, 0.24, 0.35);
      playTone(1174.66, now + 0.24, 0.26, 0.38);
      playTone(1567.98, now + 0.38, 0.65, 0.42);

      // Second harmonic echo chime
      playTone(783.99, now + 0.88, 0.18, 0.25);
      playTone(987.77, now + 1.00, 0.20, 0.28);
      playTone(1174.66, now + 1.12, 0.22, 0.30);
      playTone(1567.98, now + 1.24, 0.55, 0.35);
    };

    playTeamsChime();
    ringInterval = setInterval(playTeamsChime, 2500);
  } catch (err) {
    console.warn('Web Audio error:', err);
  }
}

function stopContinuousRingtone() {
  if (ringInterval) {
    clearInterval(ringInterval);
    ringInterval = null;
  }
  if (activeAudioCtx) {
    try {
      activeAudioCtx.close().catch(() => {});
    } catch (_) {}
    activeAudioCtx = null;
  }
}

import { getSocketBaseUrl } from 'utils/socketUrl';

export default function GlobalMeetingAlert() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeAlert, setActiveAlert] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const socketRef = useRef(null);

  useEffect(() => {
    const socketUrl = getSocketBaseUrl();
    const socket = io(socketUrl, {
      auth: (callback) => callback({ token: localStorage.getItem('authToken') || '' }),
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 30,
      reconnectionDelay: 1000
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[GlobalMeetingAlert] Connected to signaling socket at:', socketUrl);
    });

    socket.on('connect_error', (err) => {
      console.warn('[GlobalMeetingAlert] Socket connection error:', err?.message, 'at:', socketUrl);
    });

    // 1. Direct Incoming Call Pull (MS Teams Style)
    socket.on('incoming-meeting-call', (data) => {
      if (!data?.roomId) return;

      // If user is currently ALREADY inside this meeting room, do not alert
      if (location.pathname.includes(`/meetings/room/${data.roomId}`)) {
        return;
      }

      // Read current logged-in user identifiers from localStorage
      const myId = String(localStorage.getItem('userId') || '');
      const myEmpId = String(localStorage.getItem('employeeId') || '');
      const myEmpCode = String(localStorage.getItem('employeeCode') || '');
      const myName = (localStorage.getItem('userName') || '').trim().toLowerCase();
      const myEmail = (localStorage.getItem('userEmail') || '').trim().toLowerCase();

      // Robust matching: target user check
      let isTarget = false;

      if (data.targetUserId && myId && String(data.targetUserId) === myId) {
        isTarget = true;
      }
      if (data.targetEmployeeId && myEmpId && String(data.targetEmployeeId).toLowerCase() === myEmpId.toLowerCase()) {
        isTarget = true;
      }
      if (data.targetEmployeeCode) {
        const targetCode = String(data.targetEmployeeCode).toLowerCase();
        if ((myEmpCode && targetCode === myEmpCode.toLowerCase()) || (myEmpId && targetCode === myEmpId.toLowerCase())) {
          isTarget = true;
        }
      }
      if (data.targetEmail && myEmail && data.targetEmail.toLowerCase() === myEmail) {
        isTarget = true;
      }
      if (data.targetName && myName) {
        const tName = data.targetName.trim().toLowerCase();
        if (tName === myName || myName.includes(tName) || tName.includes(myName)) {
          isTarget = true;
        }
      }

      if (!isTarget) return;

      // Start continuous repeating MS Teams ringtone
      startContinuousRingtone();

      setIncomingCall(data);
    });

    // 2. General Live Broadcast Alert (When a scheduled meeting starts)
    socket.on('meeting-live-alert', (data) => {
      if (!data?.roomId) return;
      if (location.pathname.includes(`/meetings/room/${data.roomId}`)) return;

      const myId = String(localStorage.getItem('userId') || '');
      const myEmpId = String(localStorage.getItem('employeeId') || '');
      const myName = (localStorage.getItem('userName') || '').trim().toLowerCase();
      const myEmail = (localStorage.getItem('userEmail') || '').trim().toLowerCase();

      if (data.hostName && myName && data.hostName.toLowerCase() === myName) {
        return;
      }

      const invited = Array.isArray(data.participantEmployeeIds) ? data.participantEmployeeIds : [];
      // STRICT CHECK: If no employees were specifically invited, DO NOT alert anyone
      if (invited.length === 0) {
        return;
      }

      const isInvited = invited.some(
        (inv) =>
          String(inv) === myId ||
          String(inv) === myEmpId ||
          (myEmail && String(inv).toLowerCase() === myEmail) ||
          (myName && String(inv).toLowerCase() === myName)
      );
      if (!isInvited) return;

      setActiveAlert(data);
    });

    return () => {
      stopContinuousRingtone();
      socket.disconnect();
    };
  }, [location.pathname]);

  // Auto-dismiss incoming call after 45 seconds if unanswered
  useEffect(() => {
    if (incomingCall) {
      const timer = setTimeout(() => {
        stopContinuousRingtone();
        setIncomingCall(null);
      }, 45000);
      return () => {
        clearTimeout(timer);
      };
    } else {
      stopContinuousRingtone();
    }
  }, [incomingCall]);

  // Auto-dismiss general broadcast alert after 60 seconds
  useEffect(() => {
    if (activeAlert) {
      const timer = setTimeout(() => {
        setActiveAlert(null);
      }, 60000);
      return () => clearTimeout(timer);
    }
  }, [activeAlert]);

  // Handlers for Incoming Call Pull (MS Teams Style)
  const handleAcceptIncomingCall = () => {
    stopContinuousRingtone();
    if (!incomingCall) return;
    const targetRoom = incomingCall.roomId;
    setIncomingCall(null);
    navigate(`/meetings/room/${targetRoom}`);
  };

  const handleDeclineIncomingCall = () => {
    stopContinuousRingtone();
    if (socketRef.current && incomingCall) {
      socketRef.current.emit('decline-meeting-call', {
        roomId: incomingCall.roomId,
        targetEmployeeId: incomingCall.targetEmployeeId,
        targetEmployeeCode: incomingCall.targetEmployeeCode,
        targetUserId: incomingCall.targetUserId,
        targetName: localStorage.getItem('userName') || incomingCall.targetName || ''
      });
    }
    setIncomingCall(null);
  };

  // Handlers for General Broadcast Alert
  const handleJoinBroadcast = () => {
    if (!activeAlert) return;
    const targetRoom = activeAlert.roomId;
    setActiveAlert(null);
    navigate(`/meetings/room/${targetRoom}`);
  };

  const handleDismissBroadcast = () => {
    setActiveAlert(null);
  };

  return (
    <>
      {/* 1. MS Teams Signature Urgent Incoming Call Dialog */}
      {incomingCall && (
        <aside className="meet-incoming-call-overlay" role="alert" aria-live="assertive">
          <div className="meet-call-header">
            <div className="meet-call-caller-group">
              <div className="meet-call-avatar-ring">
                <PhoneOutlined />
              </div>
              <div className="meet-call-info">
                <h3 className="meet-call-title">Incoming Meeting Call</h3>
                <p className="meet-call-subtitle">
                  {incomingCall.callerName || 'A team member'} is calling you...
                </p>
              </div>
            </div>

            <button
              type="button"
              className="meet-call-close-btn"
              onClick={handleDeclineIncomingCall}
              title="Decline"
            >
              <CloseOutlined />
            </button>
          </div>

          <div className="meet-call-body">
            <p className="meet-call-meeting-name">
              &quot;{incomingCall.meetingTitle}&quot; &mdash; You have been asked to join this live meeting right now.
            </p>
            <span className="meet-call-room-badge">
              Room: {incomingCall.roomId}
            </span>
          </div>

          <div className="meet-call-actions">
            <button
              type="button"
              className="meet-call-decline-btn"
              onClick={handleDeclineIncomingCall}
            >
              <CloseOutlined />
              <span>Decline</span>
            </button>
            <button
              type="button"
              className="meet-call-accept-btn"
              onClick={handleAcceptIncomingCall}
            >
              <PhoneOutlined />
              <span>Accept &amp; Join</span>
            </button>
          </div>
        </aside>
      )}

      {/* 2. General Live Broadcast Meeting Alert */}
      {activeAlert && !incomingCall && (
        <aside className="meet-broadcast-overlay" role="status">
          <div className="meet-broadcast-header">
            <div className="meet-broadcast-host-group">
              <div className="meet-broadcast-icon-box">
                <VideoCameraOutlined />
              </div>
              <div>
                <h4 className="meet-broadcast-title">Meeting Started</h4>
                <p className="meet-broadcast-subtitle">
                  Host: {activeAlert.hostName || 'Team Host'}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="meet-call-close-btn"
              onClick={handleDismissBroadcast}
              title="Dismiss"
            >
              <CloseOutlined />
            </button>
          </div>

          <div className="meet-broadcast-body">
            <strong>{activeAlert.title}</strong> is now live. Click to join.
          </div>

          <div className="meet-broadcast-actions">
            <button
              type="button"
              className="meet-broadcast-dismiss-btn"
              onClick={handleDismissBroadcast}
            >
              Dismiss
            </button>
            <button
              type="button"
              className="meet-broadcast-join-btn"
              onClick={handleJoinBroadcast}
            >
              Join Now
            </button>
          </div>
        </aside>
      )}
    </>
  );
}
